import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useClientData } from '../hooks/useClientData';
import AddAdministratorModal from '../components/AddAdministratorModal';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface AdministratorData {
  id: number;
  name: string;
  consultantReference: string;
  taxNumber?: string;
  consultantName: string;
  consultantSurname: string;
  consultantPhone: string;
  consultantEmail: string;
  quotationsEmail?: string;
  claimsEmail?: string;
  instructionsEmail?: string;
  status: 'Active' | 'Inactive';
}

type AdministratorFormData = Omit<AdministratorData, '_id' | 'id'>;

// Confirmation Modal Component
const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  administrator 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onConfirm: () => void; 
  administrator: AdministratorData | null;
}) => {
  if (!isOpen || !administrator) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Confirm Deletion</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete administrator <strong>{administrator.name}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Administrators = () => {
  const { can } = usePermissions();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [administrators, setAdministrators] = useState<AdministratorData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [administratorToDelete, setAdministratorToDelete] = useState<AdministratorData | null>(null);
  const { id: clientId } = useParams();
  const { user, client, isLoading } = useClientData(clientId);
  const navigate = useNavigate();

  // Function to fetch the administrator data from your API
  const fetchAdministrators = async () => {
  setIsLoadingData(true);
  setError(null);
  try {
    const response = await axios.get(`${API_URL}/api/administrators`);

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = response.data;

    let admins: any[] = [];

    // Case 1: { success: true, data: [...] }
    if (body && typeof body === 'object' && body.success && Array.isArray(body.data)) {
      admins = body.data;
    }
    // Case 2: plain array [...]
    else if (Array.isArray(body)) {
      admins = body;
    }
    // Case 3: unexpected format
    else {
      throw new Error('Unexpected API response format');
    }

    // Now safely transform
    const transformed = admins.map((admin: any) => ({
      id: admin.id,                             // PostgreSQL id
      name: admin.name || '',
      consultantReference: admin.consultantReference || admin.consultant_reference || '',
      taxNumber: admin.taxNumber || '',
      consultantName: admin.consultantName || '',
      consultantSurname: admin.consultantSurname || '',
      consultantPhone: admin.consultantPhone || '',
      consultantEmail: admin.consultantEmail || '',
      quotationsEmail: admin.quotationsEmail || '',
      claimsEmail: admin.claimsEmail || '',
      instructionsEmail: admin.instructionsEmail || '',
      status: admin.status || 'Active',         // fallback
    }));

    setAdministrators(transformed);

  } catch (err: any) {
    const errorMessage = err.response?.data?.message || err.message || 'Failed to load administrators';
    setError(errorMessage);
    console.error('Error fetching administrators:', err);
  } finally {
    setIsLoadingData(false);
  }
};

  // Use the useEffect hook to call the fetch function when the component mounts
  useEffect(() => {
    fetchAdministrators();
  }, []);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

   const handleAddAdministrator = () => {
    setIsModalOpen(true);
  };

  const handleSaveAdministrator = async (formData: AdministratorFormData) => {
  try {
    console.log('Sending administrator data:', formData);
    
    const response = await axios.post(`${API_URL}/api/administrators`, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Response:', response.data);
    
    if (response.status >= 200 && response.status < 300) {
      // Check if the server returns the created administrator
      if (response.data.success && response.data.data) {
        // Refresh the administrators list
        await fetchAdministrators();
        setIsModalOpen(false);
        alert('Administrator added successfully!');
      } else {
        throw new Error('Unexpected response format');
      }
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (err) {
    console.error('Full error details:', err);
    
    // Extract more detailed error message from axios response
    let errorMessage = 'An unknown error occurred.';
    if (axios.isAxiosError(err)) {
      // Try to get error details from response
      errorMessage = err.response?.data?.error || 
                    err.response?.data?.message || 
                    err.message || 
                    'Failed to add administrator.';
      
      console.error('Server response:', err.response?.data);
      console.error('Server response status:', err.response?.status);
      
      // Log the full error for debugging
      if (err.response?.data?.details) {
        console.error('Error details:', err.response.data.details);
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
    }
    
    alert('Failed to add administrator: ' + errorMessage);
  }
};

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Function to open delete confirmation modal
  const handleDeleteClick = (administrator: AdministratorData) => {
    setAdministratorToDelete(administrator);
    setIsDeleteModalOpen(true);
  };

  // Function to close delete confirmation modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAdministratorToDelete(null);
  };

  // Function to confirm and execute deletion
  const handleConfirmDelete = async () => {
    if (!administratorToDelete) return;
    
    setDeleteLoading(true);
    try {
      
      const adminId = administratorToDelete?.id;
      
      if (!adminId) {
        throw new Error('Administrator ID is missing');
      }
      
      const response = await axios.delete(`${API_URL}/api/administrators/${adminId}`);
      
      if (response.status >= 200 && response.status < 300) {
        // Remove the deleted administrator from the state
        setAdministrators(prev => prev.filter(admin => admin.id !== administratorToDelete?.id));
        alert('Administrator deleted successfully!');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = (err instanceof Error) ? err.message : 'An unknown error occurred.';
      console.error('Error deleting administrator:', err);
      alert('Failed to delete administrator: ' + errorMessage);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setAdministratorToDelete(null);
    }
  };

  if (isLoading || isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title=""
          user={user}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        
        {/* Main content area */}
        <div className="flex-1 p-6">
          {/* Header with title and Add button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Administrators Management</h2>
            {can('insurers', 'write') && (
              <button
                onClick={handleAddAdministrator}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors font-medium flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Administrator
              </button>
            )}
          </div>
          
          {/* Modern Administrators Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Administrators List</h3>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Broker Code
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Surname
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {administrators.map((admin) => (
                        <tr key={admin.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => navigate(`/insurers/administrators/${admin.id}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              {admin.name}
                            </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600 bg-blue-100 px-2 py-1 rounded-md inline-block">
                            {admin.consultantReference}
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{admin.consultantName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{admin.consultantSurname}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{admin.consultantPhone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{admin.consultantEmail}</div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                              admin.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {admin.status}
                          </span>
                        </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {administrators.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No administrators</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding a new administrator.</p>
                {can('insurers', 'write') && (
                  <div className="mt-6">
                    <button
                      onClick={handleAddAdministrator}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Administrator
                    </button>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>
      {/* Add Administrator Modal */}
      <AddAdministratorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAdministrator}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        administrator={administratorToDelete}
      />
    </div>
  );
};

export default Administrators;